import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core'
import { NgbActiveModal, NgbCarousel, NgbCarouselConfig, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap'
import { ImageCarouselData, MappedViewportImage, ViewportImage } from './image-carousel-modal.module'
import { EventService } from '@spartacus/core'
import { ModalImageEvent } from '../gtm-event/image-events'
import { MediaQueries } from '../../../enums/media-queries'
import { ImageSize } from '../../../enums/image-size'
import { ModalSize } from '../../../enums/modal-size'
import { MbModalService } from '../../../../shared/services/modal.service'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { CmsLinkType } from '../../../enums/cms-link-type'

@Component({
  selector: 'mb-image-carousel-modal',
  templateUrl: './image-carousel-modal.component.html',
  styleUrls: ['./image-carousel-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageCarouselModalComponent implements OnInit {

  @ViewChild('carousel') carousel: NgbCarousel | undefined

  @Input() id: string | undefined
  @Input() start: number = 0
  @Input() size: ModalSize | undefined
  @Input() useCarouselButtons: boolean = false
  @Input() backgroundColor: string | undefined

  @Input() set data (data: ImageCarouselData | undefined) {
    this.data$.next(data)
  }

  @Input() set showIndicators (showIndicators: boolean) {
    if (showIndicators !== undefined && showIndicators !== null) {
      this.config.showNavigationIndicators = showIndicators
      this.showCarouselIndicators = showIndicators
    }
  }

  @Input() set endlessScroll (endlessScroll: boolean) {
    if (endlessScroll !== undefined && endlessScroll !== null) {
      this.config.wrap = endlessScroll
    }
  }

  data$ = new BehaviorSubject<ImageCarouselData | undefined>(undefined)
  currentSlide$ = new BehaviorSubject<number>(0)
  showCarouselIndicators = false

  images$ = this.data$.pipe(
    map(data => data?.viewportImages ?? []),
    map(viewportImages => viewportImages.map(viewportImage => this.mapImageSizes(viewportImage))))

  state$ = combineLatest([this.data$, this.images$, this.currentSlide$]).pipe(
    map(([data, images, currentSlide]) => ({ data, images, currentSlide } as ImageCarouselModalState)))

  constructor (private modal: NgbActiveModal,
               private events: EventService,
               private modalService: MbModalService,
               private config: NgbCarouselConfig) {
    this.config.showNavigationArrows = false
  }

  ngOnInit (): void {
    this.currentSlide$.next(this.start)
  }

  mapImageSizes (image: ViewportImage): MappedViewportImage {
    if (!image) {
      return image
    }
    image.image = this.modalService.getModalImageFromImage(image?.image, this.size ?? ModalSize.LG)
    return image as MappedViewportImage
  }

  close (): void {
    const eventAction = this.data$.value?.viewportImages?.[this.start]?.image?.title ?? ''
    this.modal.close()
    this.events.dispatch(new ModalImageEvent('Visuals', eventAction, false))
  }

  setSelectedSlide (index: number) {
    this.carousel?.select(`slide-${index}`)
  }

  onSlide (event: NgbSlideEvent): void {
    this.currentSlide$.next(+(event?.current?.replace('slide-', '') ?? 0))
  }

  slideLeft (): void {
    this.carousel?.prev()
  }

  slideRight (): void {
    this.carousel?.next()
  }

  readonly mediaQuery = MediaQueries
  readonly imageSize = ImageSize
  protected readonly cmsLinkType = CmsLinkType
}

export interface ImageCarouselModalState {
  data: ImageCarouselData | undefined;
  images: MappedViewportImage[];
  currentSlide: number;
}
